import axiosInstance from '../api/api';
import { API_ROUTES } from '../config/api-routes';

export interface OAuthResponse {
  access_token: string;
  refresh_token: string;
  user: any;
  success: boolean;
}

export interface GoogleCredentialResponse {
  credential: string;
}

export interface LinkedInResponse {
  code: string;
  state: string;
}

/**
 * Initiate Facebook OAuth redirect flow
 * Fetches authorization URL from backend and redirects user to Facebook
 */
async function fetchOAuthLinkUrl(path: string, providerLabel: string): Promise<void> {
  const response = await axiosInstance.get(path);
  if (response.data?.url) {
    window.location.href = response.data.url;
    return;
  }
  throw new Error(`Failed to get ${providerLabel} authorization URL`);
}

export const initiateGoogleOAuthLink = async (): Promise<void> => {
  try {
    await fetchOAuthLinkUrl('/auth/link/google/url', 'Google');
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(err?.response?.data?.message || 'Failed to start Google account linking');
  }
};

export const initiateLinkedInOAuthLink = async (): Promise<void> => {
  try {
    await fetchOAuthLinkUrl('/auth/link/linkedin/url', 'LinkedIn');
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(err?.response?.data?.message || 'Failed to start LinkedIn account linking');
  }
};

export const initiateFacebookOAuthLink = async (): Promise<void> => {
  try {
    await fetchOAuthLinkUrl('/auth/link/facebook/url', 'Facebook');
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(err?.response?.data?.message || 'Failed to start Facebook account linking');
  }
};

export const initiateFacebookOAuth = async (): Promise<void> => {
  try {
    const response = await axiosInstance.get('/auth/facebook/url');
    if (response.data && response.data.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error('Failed to get Facebook authorization URL');
    }
  } catch (error: any) {
    console.error('Facebook OAuth initiate error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to initiate Facebook OAuth');
  }
};

/**
 * Initiate Google OAuth redirect flow
 * Fetches authorization URL from backend and redirects user to Google
 */
export const initiateGoogleOAuth = async (mode: 'login' | 'register' = 'login'): Promise<void> => {
  try {
    const response = await axiosInstance.get('/auth/google/url', {
      params: { mode },
    });

    if (response.data && response.data.url) {
      // Redirect to Google authorization URL
      window.location.href = response.data.url;
    } else {
      throw new Error('Failed to get Google authorization URL');
    }
  } catch (error: any) {
    console.error('Google OAuth initiate error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to initiate Google OAuth');
  }
};

/**
 * Initiate LinkedIn OAuth redirect flow
 * Fetches authorization URL from backend and redirects user to LinkedIn
 */
export const initiateLinkedInOAuth = async (
  mode: 'login' | 'register' = 'login'
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/auth/linkedin/url', {
      params: { mode },
    });

    if (response.data && response.data.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error('Failed to get LinkedIn authorization URL');
    }
  } catch (error: any) {
    console.error('LinkedIn OAuth initiate error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to initiate LinkedIn OAuth');
  }
};

/**
 * Handle Google OAuth - kept for backward compatibility
 * But now just calls initiateGoogleOAuth
 */
export const handleGoogleOAuth = async (credentialResponse?: any): Promise<OAuthResponse> => {
  // This shouldn't be called anymore since we're using redirect flow
  // But keeping it to not break existing code
  await initiateGoogleOAuth();
  throw new Error('Redirecting to Google...');
};

/**
 * Handle LinkedIn OAuth login/signup
 */
export const handleLinkedInOAuth = async (code: string): Promise<OAuthResponse> => {
  try {
    const response = await axiosInstance.post(`${API_ROUTES.AUTH.LOGIN}`, {
      linkedinCode: code,
      usertype: 'candidate',
      oauthProvider: 'linkedin',
    });

    if (response.status === 200 && response.data.access_token) {
      // Store tokens
      localStorage.setItem('accessToken', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      return {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        user: response.data.user,
        success: true,
      };
    }

    throw new Error('OAuth authentication failed');
  } catch (error: any) {
    console.error('LinkedIn OAuth error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to authenticate with LinkedIn');
  }
};

/**
 * Handle LinkedIn OAuth signup
 */
export const handleLinkedInSignup = async (code: string): Promise<OAuthResponse> => {
  try {
    const response = await axiosInstance.post(`${API_ROUTES.AUTH.REGISTER}`, {
      linkedinCode: code,
      oauthProvider: 'linkedin',
    });

    if (response.status === 201 && response.data.access_token) {
      localStorage.setItem('accessToken', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      return {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        user: response.data.user,
        success: true,
      };
    }

    throw new Error('OAuth signup failed');
  } catch (error: any) {
    console.error('LinkedIn signup error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to sign up with LinkedIn');
  }
};

/**
 * Handle Google OAuth signup
 */
export const handleGoogleSignup = async (credentialResponse: any): Promise<OAuthResponse> => {
  try {
    const token = credentialResponse.credential;

    const response = await axiosInstance.post(`${API_ROUTES.AUTH.REGISTER}`, {
      googleToken: token,
      oauthProvider: 'google',
    });

    if (response.status === 201 && response.data.access_token) {
      localStorage.setItem('accessToken', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      return {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        user: response.data.user,
        success: true,
      };
    }

    throw new Error('OAuth signup failed');
  } catch (error: any) {
    console.error('Google signup error:', error);
    throw new Error(error?.response?.data?.message || 'Failed to sign up with Google');
  }
};
